<template>
  <div class="svoi-checkbox" :class="classes" @click="onClick">
    <div class="input">
      <input
        v-model="computedValue"
        class="hidden-input"
        :tabindex="tabindex"
        :disabled="disabled"
        @focus="onFocus"
        @blur="onBlur"
      />
      <SvoiIcon
        class="icon"
        :icon="computedValue ? 'essential/tick-square' : 'essential/check-square'"
        :size="iconSize"
      />
      <div class="label">
        <template v-if="label">
          {{ label }}
        </template>
        <template v-else>
          <slot />
        </template>
      </div>
    </div>
    <SvoiHint v-if="error" class="hint" theme="chilli">{{ error }}</SvoiHint>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, isVue2, ComponentPropsOptions, Ref } from 'vue-demi'
import { SvoiIcon } from '@svoi-ui/components/icon'
import { SvoiHint } from '@svoi-ui/components/hint'
import { Sizes, SizesEnum } from '@svoi-ui/interfaces/checkbox'
import { SizesEnum as IconSizesEnum } from '@svoi-ui/interfaces/icon'
import { isRefObject, isString } from '@svoi-ui/shared/utils/guards'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: Boolean,
        default: false
      }
    }
  : {
      modelValue: {
        type: Boolean,
        default: false
      }
    }

export default defineComponent({
  name: 'SvoiCheckbox',
  components: { SvoiIcon, SvoiHint },
  props: {
    ...modelProp,
    label: {
      type: String,
      default: ''
    },
    size: {
      type: String as PropType<Sizes | string>,
      default: SizesEnum.l,
      validator(value: unknown) {
        return isString(value) && [SizesEnum.l.toString(), SizesEnum.m.toString()].includes(value)
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String as PropType<string | Ref<string>>,
      default: ''
    },
    tabindex: {
      type: Number,
      default: 0
    }
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const focus = ref(false)
    const computedValue = computed<boolean>({
      get() {
        return isVue2 ? !!props.value : !!props.modelValue
      },
      set(val) {
        emit(isVue2 ? 'input' : 'update:modelValue', val)
      }
    })

    const classes = computed(() => ({
      '-checked': computedValue.value,
      '-disabled': props.disabled,
      '-focus': isRefObject(focus) && focus.value,
      [`-size-${props.size}`]: true
    }))

    const iconSize = computed(() => (props.size === SizesEnum.l ? IconSizesEnum.m : IconSizesEnum.s))

    const onClick = () => {
      if (!props.disabled) {
        computedValue.value = !computedValue.value
      }
    }

    const onFocus = () => {
      if (!props.disabled) {
        focus.value = true
      }
    }

    const onBlur = () => {
      focus.value = false
    }

    return { computedValue, classes, iconSize, onClick, onFocus, onBlur }
  }
})
</script>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/checkbox.scss';
</style>
